import React, { FC } from 'react';

import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  ANNUAL_PLAN_DISCOUNT,
  ANNUAL_PLAN_OLD_PRICE,
} from '~/constants/products';
import { getDescriptionBenefits } from '~/modules/settings/components/modals/UpgradeSubscriptionCheckout/variants/constants';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { colors } from '~/theme/colors';
import { Typographies } from '~/theme/typography';
import { ProductType } from '~/types/product';

import styles from './styles.module.scss';

export type OrderDescriptionProps = {
  offeredProduct: ProductType;
  annualPlanDiscount?: string;
  annualPlanOldPrice?: string;
};

const OrderDescription: FC<OrderDescriptionProps> = ({
  offeredProduct,
  annualPlanDiscount = ANNUAL_PLAN_DISCOUNT,
  annualPlanOldPrice = ANNUAL_PLAN_OLD_PRICE,
}) => {
  const { user_subscription, user_product } = useAppSelector(
    (state) => state.profile,
  );

  const { t } = useTranslation('general');

  const benefits = getDescriptionBenefits({
    t,
    isTrial: user_subscription.is_trial,
  });

  return (
    <div className={styles.container}>
      <Typography
        variant={Typographies.TITLE_LARGE}
        component="div"
        className={styles.title}
      >
        <Icon icon="material-symbols:all-inclusive" width="32" height="32" />
        <span>{offeredProduct.name}</span>
      </Typography>

      <div className={styles.benefits__list}>
        {benefits.map((item) => {
          return (
            <Typography
              key={item}
              variant={Typographies.BODY_LARGE}
              component="div"
              className={styles.benefits__item}
            >
              <Icon
                icon="material-symbols:check-rounded"
                width="24"
                height="24"
                color={colors.green_50}
              />
              <span>{item}</span>
            </Typography>
          );
        })}
      </div>

      <Typography
        variant={Typographies.TITLE_LARGE}
        component="div"
        className={styles.price__new}
      >
        {(offeredProduct.price / 100 / 12).toFixed(2)} {offeredProduct.currency}
        /{t('month', 'Month').toLowerCase()}
      </Typography>

      <Typography
        variant={Typographies.LABEL_LARGE}
        component="div"
        className={styles.price__old}
      >
        <div>
          {(offeredProduct.price / 100).toFixed(2)} {offeredProduct.currency}/
          {t(
            `${offeredProduct.regularity}`,
            offeredProduct.regularity,
          ).toLowerCase()}
        </div>
        <div className={styles.crossed}>{annualPlanOldPrice}</div>
      </Typography>

      <Typography
        variant={Typographies.LABEL_LARGE}
        component="div"
        className={styles.label}
      >
        {t('modal:upgrade_sub.label_v2', {
          amount: annualPlanDiscount,
          defaultValue: 'Save {{amount}} Annually',
        })}
      </Typography>

      {user_subscription.is_trial && (
        <Typography
          variant={Typographies.BODY_MEDIUM}
          component="div"
          className={styles.trial}
        >
          {t('modal:upgrade_sub.trial', {
            trial_duration: user_product.trial_period,
            defaultValue:
              'Alternatively, at the end of the {{trial_duration}}-day introductory period, your plan will automatically upgrade to the default plan',
          })}{' '}
          ({(user_product.price / 100).toFixed(2)} {user_product.currency}/
          {t(
            `${user_product.regularity}`,
            user_product.regularity,
          ).toLowerCase()}
          )
        </Typography>
      )}
    </div>
  );
};

export default OrderDescription;
